import { QueryClient } from '@tanstack/react-query'
import { http, createConfig } from 'wagmi'
import { base, mainnet } from 'wagmi/chains'
import { injected } from 'wagmi/connectors'
import { BASE_CHAIN_ID_NUMBER, ETH_CHAIN_ID_NUMBER } from '@mprolab/sdk'
import { getRpc } from 'connectors/utils'
import { normalizeRpcMapForWalletConnect } from 'utils/walletConnectRpcMap'
import { walletConnectionStore as walletStore } from 'mobxStores/WalletConnectionStore'
import { walletConnectWithUri } from 'lib/walletConnectWithUri'
import { getRpcUrlByChainId } from 'utils/getRpcUrl'

const WC_OPTIONAL_CHAINS = [ETH_CHAIN_ID_NUMBER, BASE_CHAIN_ID_NUMBER] as number[]

function httpUrlForChain(chainId: number): string {
  const pathOrUrl = getRpcUrlByChainId(chainId)
  if (pathOrUrl.startsWith('http')) return pathOrUrl
  if (typeof window !== 'undefined') return new URL(pathOrUrl, window.location.origin).href
  return pathOrUrl
}

function buildWalletConnectRpcMap(): Record<number, string> {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return normalizeRpcMapForWalletConnect(getRpc(), origin)
}

function buildConnectors() {
  const rpcMap = buildWalletConnectRpcMap()
  const list = [
    injected({
      shimDisconnect: true,
    }),
  ]

  const wcProjectId = String(import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID ?? '').trim()
  const onWcDisplayUri = (uri: string) => walletStore.setWcPairingUri(uri)

  if (wcProjectId) {
    list.push(
      walletConnectWithUri({
        projectId: wcProjectId,
        optionalChains: WC_OPTIONAL_CHAINS,
        rpcMap,
        onDisplayUri: onWcDisplayUri,
        connectorId: 'walletConnect',
        connectorName: 'WalletConnect',
      }) as ReturnType<typeof injected>,
    )
  }

  return list
}

export const wagmiConfig = createConfig({
  chains: [mainnet, base],
  connectors: buildConnectors(),
  transports: {
    [mainnet.id]: http(httpUrlForChain(mainnet.id)),
    [base.id]: http(httpUrlForChain(base.id)),
  },
  ssr: false,
})

export const wagmiQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      retry: 2,
    },
  },
})
